import { type AnyRouter, deepEqual } from "@tanstack/react-router"
import { applyPushTap, sendNotificationResponse } from "@/entities/notification"
import { takeOpenedNotifications } from "@/shared/api/websocket"

// deepEqual(a, b, partial, explicitUndefined)
const ONLY_TARGET_KEYS = true
const STRICT_UNDEFINED = true

/**
 * One interception point instead of per-screen wiring. A notification counts as
 * opened only when the user tapped its push or Telegram button and landed on
 * the screen it points at — that lands here whether the payload arrived before
 * the navigation or after it. Walking onto the same screen unprompted is left alone: it says
 * nothing about the notification, and the profile is a tab people open anyway.
 */
export function confirmOpenedNotifications(router: AnyRouter): void {
	const { location, matches } = router.state
	const leaf = matches.at(-1)
	if (!leaf) return

	const opened = takeOpenedNotifications((route) => {
		const target = router.buildLocation(route)
		if (target.pathname !== location.pathname) return false

		// Compare validated search on both sides: the built target has its
		// defaults stripped (`/feed` is the cards tab). Only the target's keys count.
		const targetSearch =
			router.matchRoutes(target.pathname, target.search).at(-1)?.search ?? {}
		return deepEqual(
			leaf.search,
			targetSearch,
			ONLY_TARGET_KEYS,
			STRICT_UNDEFINED,
		)
	})
	const onHomeScreen = location.pathname === "/home"
	for (const { notificationId, source, responseName, routePopup } of opened) {
		applyPushTap(notificationId, onHomeScreen)
		sendNotificationResponse(responseName, "opened", notificationId, source)
		if (routePopup !== null) routePopup()
	}
}
